import { useCallback, useMemo } from "react";
import { useTypedSearchParams } from "react-router-typesafe-routes";

import { useGroupList } from "~/features/admin/groups/hooks";
import { toGroupRowList } from "~/features/admin/groups/utils/transformers";
import { v2Route } from "~/v2/routes";
import { GroupsPresenter } from "./groups.presenter";

const DEFAULT_PAGE_SIZE = 20;

export function GroupsContainer() {
  const [params, setParams] = useTypedSearchParams(v2Route.admin.groups);
  const { data, refetch } = useGroupList();

  const rows = useMemo(() => toGroupRowList(data.userGroups), [data.userGroups]);

  const pageIndex = params.page ?? 0;
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
  const selectedGroup = params.selectedGroup ?? null;

  const onPageReset = useCallback(() => {
    setParams((prev) => ({ ...prev, page: undefined }));
  }, [setParams]);

  const onPageIndexChange = useCallback(
    (next: number) => {
      setParams((prev) => ({ ...prev, page: next === 0 ? undefined : next }));
    },
    [setParams],
  );

  const onPageSizeChange = useCallback(
    (next: number) => {
      setParams((prev) => ({
        ...prev,
        pageSize: next === DEFAULT_PAGE_SIZE ? undefined : next,
        page: undefined,
      }));
    },
    [setParams],
  );

  const onSelect = useCallback((id: string) => setParams((prev) => ({ ...prev, selectedGroup: id })), [setParams]);

  const onCloseSelected = useCallback(() => setParams((prev) => ({ ...prev, selectedGroup: undefined })), [setParams]);

  const onRefresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  return (
    <GroupsPresenter
      rows={rows}
      pageIndex={pageIndex}
      onPageIndexChange={onPageIndexChange}
      pageSize={pageSize}
      onPageSizeChange={onPageSizeChange}
      onPageReset={onPageReset}
      onRefresh={onRefresh}
      selectedGroup={selectedGroup}
      onSelect={onSelect}
      onCloseSelected={onCloseSelected}
    />
  );
}
